<script setup lang="ts">
import type { TPricing } from "~/components/cards/Pricing.vue";
import { cloudPbxPrices, cloudPbxComparePlans } from "~/data/prices/cloud-pbx";

const props = defineProps<{
    title: string,
    desc: string,
}>();
const isShowComparePlans = ref(false);
const eightPrefixUserCount = ref(10);
const ninePrefixUserCount = ref(10);

const eightPrefix = computed(() => {
  return cloudPbxPrices
    .filter(item => item.prefix === '8')
    .map(pricing => ({
        ...pricing,
        amounts: pricing.amounts.map(amount => resolveAmountPrice(amount, eightPrefixUserCount.value))
    }));
});

const ninePrefix = computed(() => {
  return cloudPbxPrices
    .filter(item => item.prefix === '9')
    .map(pricing => ({
        ...pricing,
        amounts: pricing.amounts.map(amount => resolveAmountPrice(amount, ninePrefixUserCount.value))
    }));
});


function resolveAmountPrice(amount: TPricing['amounts'][number], userCount: number) {
    if (!Array.isArray(amount.value)) return { ...amount };
    const matchedTier = amount.value.find(item => {
        const min = item.min ?? 1;
        const max = item.max ?? Infinity;
        return userCount >= min && userCount <= max;
    });

    if (!matchedTier) return { ...amount };
    return { 
        ...amount, 
        value: formatPrice(matchedTier.price) 
    };
};
</script>

<template>
    <SectionsPricesBase :title="props.title" :desc="props.desc">
        <div class="w-full flex-center flex-col gap-20 my-12 lg:mb-15">
            <div class="yellow-label text-body1-bold">
                سرشماره 9 (پیش شماره‌ 9169)
            </div>
            <SectionsPricesCloudPbxUsersRange v-model="ninePrefixUserCount" />
        </div>
        <div class="w-full flex flex-col lg:flex-row items-center justify-center lg:items-start gap-10 lg:gap-6">
            <LazyCardsPricing
                v-if="ninePrefix"
                v-for="item in ninePrefix"
                :key="item.title"
                :item="item"
                class="max-w-82.5!"
            />
        </div>
        <div class="w-full flex-center flex-col gap-20 my-12 lg:mb-15">
            <div class="yellow-label text-body1-bold">
                سرشماره 8 (پیش شماره‌های 8280 و 8164 و 8721)
            </div>
            <SectionsPricesCloudPbxUsersRange v-model="eightPrefixUserCount" />
        </div>
        <div class="w-full flex flex-col lg:flex-row items-center justify-center lg:items-start gap-10 lg:gap-6 mt-10 lg:mt-15">
            <LazyCardsPricing
                v-if="eightPrefix"
                v-for="item in eightPrefix"
                :key="item.title"
                :item="item"
                class="max-w-82.5!"
            />
        </div>
        <Accordion v-model="isShowComparePlans" class="my-8">
            <template #head="{ isOpen, toggleAccordion }">
                <Button
                    type="Secondary"
                    :append-icon="{ 
                        name: isOpen 
                            ? 'solar:round-alt-arrow-up-bold' 
                            : 'solar:round-alt-arrow-down-bold' 
                    }"
                    pill
                    class="mx-auto"
                    @click="toggleAccordion"
                >
                    مقایسه ویژگی‌ها
                </Button>
            </template>
            <template #body>
                <LazySectionsPricesComparePlans 
                    v-if="isShowComparePlans" 
                    :plans="cloudPbxComparePlans"
                    class="my-8"
                />
            </template>
        </Accordion>
        <LazyVisibleSectionsPricesCloudPbxCompareWithTCI hydrate-on-visible class="mt-8" />
    </SectionsPricesBase>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.yellow-label {
    @apply
    w-fit lg:w-91.25 flex justify-center items-center mx-auto py-2 px-2 lg:px-0 bg-secondary-600 
    rounded-full shadow-gray-m text-center
}
</style>